/**
 * Time, at the bottom of the stage (ui/DESIGN.md §3.4): the range as pills, a histogram of what was found over
 * the whole window (every bucket drawn, empty ones as a baseline; failed buckets in --fail), a draggable as-of
 * handle that greys everything newer (the map through its asOf prop, the lenses through AsOfContext), the
 * window's start at the left and "now" at the right, and ▶: the story of a single record, or a replay of the
 * window that sweeps the handle from the start to now in nine seconds with the moving date under it.
 * It floats at the bottom of the screen while the map is the last thing on the page (`sticky`), and sits as the
 * last row of the map's surface while a lens is open under it, so a lens's header is never covered.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import dayjs from 'dayjs';
import type { HistogramBucket } from '../lib/api.ts';
import { age } from '../lib/freshness.ts';
import { Pill, Surface, cx } from '../design/primitives.tsx';
import { PillTrack } from './lenses/common.tsx';
import { RANGES, type Range } from './scope.ts';

const RANGE_LABEL: Record<Range, string> = { '24h': '24 h', '7d': '7 d', '30d': '30 d', all: 'all' };
const REPLAY_MS = 9000;
/** Buckets drawn across the track at most: finer histograms are summed into wider bars. */
const MAX_BARS = 200;
const clamp = (v: number, a = 0, b = 1) => Math.min(b, Math.max(a, v));

/** "6h" → ms; null when the cloud did not say. */
export function intervalMs(s: string | null | undefined): number | null {
  const m = /^(\d+)([smhdw])$/.exec(s ?? '');
  if (!m) return null;
  return Number(m[1]) * { s: 1000, m: 60_000, h: 3_600_000, d: 86_400_000, w: 7 * 86_400_000 }[m[2] as 's' | 'm' | 'h' | 'd' | 'w'];
}

export function Scrubber({ range, onRange, span: own, start, now, asOf, onAsOf, histogram, interval, failedAt, single, onStory, sticky = true, className, style }: {
  /** The range pill that is active: one of RANGES, or 'span' for a single record's own span. */
  range: string;
  onRange: (r: Range) => void;
  /** A single record's own span ("13 h", "16 d"; the real span): a pill of its own, active while the stage fits it; a click fits it again. */
  span?: { label: string; onPick: () => void } | null;
  /** The window the track spans: its start and now. */
  start: number;
  now: number;
  /** The as-of moment (null = now); `commit` is false while dragging or replaying. */
  asOf: number | null;
  onAsOf: (t: number | null, commit: boolean) => void;
  histogram: HistogramBucket[];
  interval: string | null;
  /** When the failed runs started: their buckets are drawn in --fail. */
  failedAt: number[];
  /** A single record is in focus: ▶ tells its story instead of replaying the window. */
  single: boolean;
  onStory: () => void;
  /** Floating over the bottom of the screen (default), or a plain row inside the map's surface. */
  sticky?: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [replaying, setReplaying] = useState(false);
  const raf = useRef(0);
  const commitTimer = useRef(0);
  const span = Math.max(1, now - start);
  const frac = asOf === null ? 1 : clamp((asOf - start) / span);
  // A key press moves the handle a bucket, or a 48th of the window when the buckets are coarser than that
  const step = Math.max(60_000, Math.min(intervalMs(interval) ?? Infinity, span / 48));

  const at = (clientX: number): number | null => {
    const r = track.current?.getBoundingClientRect();
    if (!r || r.width === 0) return null;
    const f = clamp((clientX - r.left) / r.width);
    return f >= 0.985 ? null : Math.round(start + f * span);
  };
  const down = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    stopReplay();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    onAsOf(at(e.clientX), false);
  };
  const move = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    onAsOf(at(e.clientX), false);
  };
  const up = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    setDragging(false);
    onAsOf(at(e.clientX), true);
  };
  // Keys move the handle at once and write the URL a moment later
  const nudge = (t: number | null) => {
    onAsOf(t, false);
    window.clearTimeout(commitTimer.current);
    commitTimer.current = window.setTimeout(() => onAsOf(t, true), 350);
  };
  const key = (e: KeyboardEvent<HTMLDivElement>) => {
    const cur = asOf ?? now;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') nudge(Math.max(start, cur - step));
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') nudge(cur + step >= now - step / 2 ? null : cur + step);
    else if (e.key === 'Home') nudge(start);
    else if (e.key === 'End') nudge(null);
    else return;
    e.preventDefault();
    stopReplay();
  };

  const stopReplay = () => {
    cancelAnimationFrame(raf.current);
    setReplaying(false);
  };
  const replay = () => {
    if (replaying) {
      stopReplay();
      onAsOf(asOf, true);
      return;
    }
    // From where the handle is (or the start) to now, in nine seconds for the whole window
    const from = asOf === null ? start : asOf;
    const begin = performance.now();
    const dur = REPLAY_MS * ((now - from) / span);
    setReplaying(true);
    const tick = (t: number) => {
      const p = dur > 0 ? clamp((t - begin) / dur) : 1;
      if (p >= 1) {
        onAsOf(null, true);
        setReplaying(false);
        return;
      }
      onAsOf(Math.round(from + (now - from) * p), false);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  };
  useEffect(() => () => {
    cancelAnimationFrame(raf.current);
    window.clearTimeout(commitTimer.current);
  }, []);

  const bars = useMemo(() => barsOf(histogram, interval, failedAt, start, now), [histogram, interval, failedAt, start, now]);
  const label = `as of ${asOf === null ? 'now' : dayjs(asOf).isSame(dayjs(now), 'day') ? dayjs(asOf).format('HH:mm') : dayjs(asOf).format('ddd HH:mm')}`;
  const date = asOf === null ? '' : dayjs(asOf).format('ddd D MMM HH:mm');

  const inner = (
    <>
        <PillTrack className="shrink-0">
          {own && <Pill active={range === 'span'} onClick={own.onPick}>{own.label}</Pill>}
          {RANGES.map((r) => <Pill key={r} active={range === r} onClick={() => onRange(r)}>{RANGE_LABEL[r]}</Pill>)}
        </PillTrack>
        <div ref={track} className="stage-track flex-1 mx-2" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
          <div className="stage-track-line" aria-hidden="true" />
          <div className="stage-hist" aria-hidden="true">
            {bars.map((b) => (
              <i key={b.time} className={cx(b.failed && 'fail', b.h === 0 && 'empty')} style={{ left: `${b.left * 100}%`, width: `${b.width * 100}%`, height: b.h > 0 ? `${b.h * 100}%` : 2 }} />
            ))}
          </div>
          {asOf !== null && <div className="stage-future" style={{ width: `${(1 - frac) * 100}%` }} aria-hidden="true" />}
          <div className={cx('stage-handle', replaying && 'replaying')} role="slider" tabIndex={0} aria-label="As of" aria-valuemin={start} aria-valuemax={now} aria-valuenow={asOf ?? now}
            aria-valuetext={asOf === null ? 'now' : date} onKeyDown={key} style={{ left: `${frac * 100}%` }}>
            <span className={cx('stage-handle-label', asOf === null && 'idle', frac > 0.9 && 'at-end', frac < 0.06 && 'at-start')}>{label}</span>
            <span className="stage-knob" />
            {replaying && asOf !== null && <span className={cx('stage-handle-date', frac > 0.85 && 'at-end', frac < 0.12 && 'at-start')}>{date}</span>}
          </div>
          {frac > 0.08 && <span className="stage-start" aria-hidden="true">{age(start, now)} ago</span>}
          {asOf !== null && frac < 0.9 && <span className="stage-now" aria-hidden="true">now</span>}
        </div>
        <button type="button" onClick={single ? onStory : replay} aria-label={single ? 'Tell the story' : replaying ? 'Pause the replay' : 'Replay the window'} aria-pressed={replaying}
          className="shrink-0 size-10 rounded-full grid place-items-center bg-n10 text-n1 border-0 cursor-pointer transition-transform duration-[var(--t-micro)] ease-mv active:scale-95">
          {replaying ? (
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M3.5 2.5h2.5v9H3.5zM8 2.5h2.5v9H8z" fill="currentColor" /></svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M3.5 2v10l8.5-5z" fill="currentColor" /></svg>
          )}
        </button>
    </>
  );
  if (!sticky) return <div className={cx('stage-scrubber-row stage-dim flex items-center gap-4 pl-2 pr-2 py-1', className)} style={style}>{inner}</div>;
  return (
    <div className={cx('sticky bottom-4 z-[35] stage-dim', className)} style={style}>
      <Surface level={2} radius="stage" className="flex items-center gap-4 pl-2 pr-2 py-2 bg-glass backdrop-blur-md">{inner}</Surface>
    </div>
  );
}

interface Bar { left: number; width: number; h: number; failed: boolean; time: number }

/**
 * The bars of the track: one per bucket over the whole window, from its start to now, so an empty week is a flat
 * baseline and not a missing one. The cloud's buckets (which only span what was found) are summed into them; the
 * buckets grow wider when the window holds more than MAX_BARS of them.
 */
export function barsOf(histogram: HistogramBucket[], interval: string | null, failedAt: number[], start: number, now: number): Bar[] {
  const span = Math.max(1, now - start);
  const fine = intervalMs(interval) ?? (histogram.length > 1 ? histogram[1].time - histogram[0].time : 0);
  const base = fine > 0 ? fine : span / 48;
  const bucket = base * Math.max(1, Math.ceil(span / base / MAX_BARS));
  const first = Math.floor(start / bucket) * bucket;
  const n = Math.min(MAX_BARS * 2, Math.ceil((now - first) / bucket));
  const counts = new Array<number>(n).fill(0);
  for (const b of histogram) {
    const i = Math.floor((b.time - first) / bucket);
    if (i >= 0 && i < n) counts[i] += b.count;
  }
  const failed = new Array<boolean>(n).fill(false);
  for (const t of failedAt) {
    const i = Math.floor((t - first) / bucket);
    if (i >= 0 && i < n) failed[i] = true;
  }
  const max = Math.max(1, ...counts);
  const width = Math.max(0.002, Math.min(1, bucket / span) - 0.0015);
  return counts.map((c, i) => {
    const time = first + i * bucket;
    return { time, left: clamp((Math.max(time, start) - start) / span), width: Math.min(width, clamp((time + bucket - start) / span) - clamp((time - start) / span)), h: c > 0 ? Math.max(0.12, c / max) : 0, failed: failed[i] };
  });
}
